/**
 * Impresión de comprobantes de Ventas x Mayor (iframe oculto + window.print).
 *
 * Compartido por el mantenimiento de facturas, el detalle unificado de
 * factura, los conduces consignados y los cobros: factura con líneas/cuotas,
 * conduce de entrega en consignación y recibo de cobro.
 */

import { encabezadoEmpresaHtml } from "lib/impresionhtml";

export const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const monedaLabel = (m) => (m === "U" ? "USD" : "RD$");

// Año del vehículo (producto.ano, numérico): sin año grabado llega 0 o
// "0000" y se deja en blanco.
const anoTexto = (v) => (Number(v) > 0 ? String(v).trim() : "");

// La cuota 0 de una factura x mayor es su inicial pactado (backend:
// ventasxmayorbackend.CUOTA_INICIAL); las demás se muestran por su número.
// Cuotas viejas sin número (importadas) quedan en blanco.
export const esCuotaInicial = (n) => n === 0 || n === "0";
export const etiquetaCuota = (n) => (esCuotaInicial(n) ? "Inicial" : n ?? "");

function imprimirHtml(html) {
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

// Logo y pie de página del membrete: solo data URLs de imagen (lo que guarda
// configuración); cualquier otra cosa se ignora para no inyectar HTML en la
// impresión (mismo criterio que impresionfacturamotor.js).
const logoImg = (comp) =>
  comp && typeof comp.logo === "string" && comp.logo.startsWith("data:image/")
    ? `<img class="logo" src="${comp.logo}" alt="" />`
    : "";

const tienePie = (comp) =>
  comp && typeof comp.footer === "string" && comp.footer.startsWith("data:image/");

// Ancho y alto en px de una imagen data: (PNG, JPEG, GIF o WebP) leídos de
// su cabecera, sin cargarla. null si el formato no se reconoce.
function dimensionesImagen(dataUrl) {
  try {
    const bin = atob(dataUrl.slice(dataUrl.indexOf(",") + 1));
    const u = (i) => bin.charCodeAt(i);
    const be16 = (i) => u(i) * 256 + u(i + 1);
    const le16 = (i) => u(i) + u(i + 1) * 256;
    const le24 = (i) => le16(i) + u(i + 2) * 65536;
    const be32 = (i) => be16(i) * 65536 + be16(i + 2);
    let dim = null;
    if (bin.startsWith("\x89PNG")) dim = { w: be32(16), h: be32(20) };
    else if (bin.startsWith("GIF8")) dim = { w: le16(6), h: le16(8) };
    else if (bin.startsWith("RIFF") && bin.slice(8, 12) === "WEBP") {
      const tipo = bin.slice(12, 16);
      if (tipo === "VP8 ") dim = { w: le16(26) % 16384, h: le16(28) % 16384 };
      else if (tipo === "VP8L") {
        const bits = le16(21) + le16(23) * 65536;
        dim = { w: (bits % 16384) + 1, h: (Math.floor(bits / 16384) % 16384) + 1 };
      } else if (tipo === "VP8X") dim = { w: le24(24) + 1, h: le24(27) + 1 };
    } else if (u(0) === 0xff && u(1) === 0xd8) {
      // JPEG: recorre los segmentos hasta el SOF (C0–CF salvo C4, C8 y CC).
      let i = 2;
      while (i + 9 < bin.length && !dim) {
        if (u(i) !== 0xff) {
          i += 1;
        } else {
          const m = u(i + 1);
          if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
            dim = { w: be16(i + 7), h: be16(i + 5) };
          } else {
            i += 2 + be16(i + 2);
          }
        }
      }
    }
    return dim && dim.w > 0 && dim.h > 0 ? dim : null;
  } catch (e) {
    return null;
  }
}

// Pie de página del membrete. Antes iba con position: fixed al fondo de la
// hoja, encima del contenido: en una factura larga las últimas filas (el plan
// de cuotas) quedaban debajo del pie y, como el contenido "cabía" en la hoja,
// no pasaban a la siguiente. Ahora el pie va en el margen inferior de la
// página (@page @bottom-center), que Chrome repite en cada hoja y que el
// contenido nunca invade: el margen se agranda al alto del pie, calculado con
// la proporción de la imagen sobre el ancho útil de la hoja carta. (Reservar
// el espacio con un <tfoot> repetido no sirve: Chrome no repite pies de tabla
// más altos que un cuarto de la hoja, y los logos suelen serlo.) Si no se
// reconoce el formato de la imagen, queda el pie fijo de antes.
const footerFijo = (comp) =>
  tienePie(comp) ? `<div class="pie"><img src="${comp.footer}" alt="" /></div>` : "";

const ANCHO_UTIL_MM = 215.9 - 28; // carta menos márgenes de 14 mm

const paginaConPie = (comp) => {
  const dim = tienePie(comp) ? dimensionesImagen(comp.footer) : null;
  if (!dim) return { css: "@page { size: letter; margin: 14mm; }", pie: footerFijo(comp) };
  const alto = (ANCHO_UTIL_MM * dim.h) / dim.w;
  // image-set con resolución: la imagen se pinta al ancho útil (px por px CSS).
  const resolucion = dim.w / ((ANCHO_UTIL_MM / 25.4) * 96);
  return {
    css: `@page { size: letter; margin: 14mm 14mm ${(14 + alto + 3).toFixed(2)}mm 14mm;
        @bottom-center { content: image-set(url("${comp.footer}") ${resolucion.toFixed(4)}x);
          width: ${ANCHO_UTIL_MM}mm; height: ${alto.toFixed(2)}mm; vertical-align: bottom; } }`,
    pie: "",
  };
};

// Comprobante de la factura x mayor en hoja carta (8.5x11) con el estilo de
// la casa. Las líneas devueltas (consignado) salen tachadas con su fecha de
// devolución. `factura.monedaTexto` permite pasar la etiqueta ya resuelta.
export function imprimirFactura(det) {
  const f = det.factura || {};
  const compania = det.compania || {};
  const lineas = det.lineas || [];
  const cuotas = det.cuotas || [];
  const mon = f.monedaTexto || monedaLabel(f.moneda);
  const pagina = paginaConPie(compania);
  const NAVY = "#18586b";
  // La columna Estado solo sale cuando hay devoluciones (consignado): el
  // Pagado/Pendiente por chasis es control interno y no va en la factura.
  const conEstado = lineas.some((l) => l.devuelto === "S");
  const filasLineas = lineas
    .map((l, i) => {
      const devuelto = l.devuelto === "S";
      const estado = devuelto ? (l.fechadevuelto ? `Devuelto ${l.fechadevuelto}` : "Devuelto") : "";
      return `<tr class="${devuelto ? "devuelto" : ""}">
          <td>${i + 1}</td>
          <td>${l.chasis || ""}</td>
          <td>${l.maquina || ""}</td>
          <td>${l.marca || ""}</td>
          <td>${l.modelo || ""}</td>
          <td>${l.colore || ""}</td>
          <td>${anoTexto(l.ano)}</td>
          <td class="num">${fmtMoney(l.preciomotor ?? l.precio)}</td>
          ${conEstado ? `<td class="estado${devuelto ? " rojo" : ""}">${estado}</td>` : ""}
        </tr>`;
    })
    .join("");
  // Inicial pactado (cuota 0): lo trae la cabecera en la reimpresión y la
  // pantalla de facturar; si no, se deduce de la propia cuota.
  const c0 = cuotas.find((c) => esCuotaInicial(c.numero_pago ?? c.numero));
  const inicialPactado = Number(f.inicialpactado ?? c0?.valor ?? 0) || 0;
  const inicialPagado = Number(f.inicialpagado ?? c0?.valor_pagado ?? 0) || 0;
  const filaResumen = (etiqueta, valor) =>
    `<tr><td>${etiqueta}</td><td class="num">${mon} ${fmtMoney(valor)}</td></tr>`;
  const resumenInicial =
    inicialPactado > 0
      ? `<table class="resumen">
        ${filaResumen("Inicial pactado", inicialPactado)}
        ${filaResumen(
          `Inicial pagado${f.reciboinicial ? ` (recibo #${f.reciboinicial})` : ""}`,
          inicialPagado
        )}
        ${filaResumen("Inicial pendiente", Math.max(0, inicialPactado - inicialPagado))}
        ${filaResumen("Financiado en cuotas", Math.max(0, Number(f.valor || 0) - inicialPactado))}
      </table>`
      : "";
  const filasCuotas = cuotas
    .map(
      (c) => `<tr>
          <td>${etiquetaCuota(c.numero_pago ?? c.numero)}</td>
          <td>${c.fecha || ""}</td>
          <td class="num">${fmtMoney(c.valor)}</td>
        </tr>`
    )
    .join("");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Factura Ventas x Mayor</title>
    <style>
      ${pagina.css}
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 90px; max-width: 250px; object-fit: contain; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .cliente { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                 display: flex; gap: 30px; flex-wrap: wrap; }
      .cliente span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .cliente b { font-size: 12px; }
      /* Solo las líneas de los chasis van con letra reducida (más columnas
         desde que salió el número de máquina); el resto del comprobante
         conserva su tamaño. */
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 5px 7px; font-size: 10px; text-align: left; }
      table.lineas td { padding: 5px 7px; border-bottom: 1px solid #e3e9ee; font-size: 10px; }
      table.lineas tr:nth-child(even) td { background: #f5f8fa; }
      th.num, td.num { text-align: right; }
      td.estado { text-align: center; }
      td.rojo { color: #C62828; font-weight: bold; }
      tr.devuelto td { color: #999; text-decoration: line-through; }
      tr.devuelto td.rojo { color: #C62828; text-decoration: none; }
      .total { margin: 12px 0; text-align: right; }
      .total .monto { display: inline-block; background: #f0f5f8; border: 1px solid #d7dee5; border-radius: 6px;
                      padding: 8px 16px; font-size: 14px; font-weight: bold; color: ${NAVY}; }
      .cuotas-titulo { margin: 16px 0 4px; font-weight: bold; color: ${NAVY}; font-size: 12px; text-transform: uppercase; }
      table.cuotas { width: 55%; border-collapse: collapse; }
      table.cuotas th { background: #f0f5f8; color: ${NAVY}; padding: 5px 8px; font-size: 11px; text-align: left; }
      table.cuotas td { padding: 4px 8px; border-bottom: 1px solid #e3e9ee; }
      table.lineas th.num, table.cuotas th.num { text-align: right; }
      table.resumen { margin: 0 0 0 auto; border-collapse: collapse; min-width: 300px; }
      table.resumen td { padding: 3px 8px; border-bottom: 1px solid #e3e9ee; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
      .pie { position: fixed; bottom: 0; left: 0; right: 0; }
      .pie img { display: block; width: 100%; }
      table.lineas tr, table.cuotas tr, table.resumen tr, .firmas { break-inside: avoid; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(compania)}
          <div>
            <h1>${compania.nombre || ""}</h1>
            ${compania.direccion ? `<p>${compania.direccion}</p>` : ""}
            <p>${[
              compania.rnc ? `RNC: ${compania.rnc}` : "",
              compania.telefono ? `Tel: ${compania.telefono}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Factura Ventas x Mayor</div>
          <p>${f.tipofactura || ""}</p>
          <p># Factura: <b>${f.id || ""}</b></p>
          <p>Fecha: <b>${f.fecha || ""}</b></p>
          ${f.ncf ? `<p>NCF: <b>${f.ncf}</b></p>` : ""}
          ${f.conduce ? `<p>Conduce: <b>#${f.conduce}</b></p>` : ""}
        </div>
      </div>
      <div class="cliente">
        <div><span>Cliente</span><b>${f.cliente || ""}</b></div>
        ${f.rnc ? `<div><span>RNC</span><b>${f.rnc}</b></div>` : ""}
        ${f.concepto ? `<div><span>Concepto</span><b>${f.concepto}</b></div>` : ""}
      </div>
      <table class="lineas">
        <tr>
          <th style="width:28px">#</th>
          <th>Chasis</th>
          <th>Máquina</th>
          <th>Marca</th>
          <th>Modelo</th>
          <th>Color</th>
          <th>Año</th>
          <th class="num">Precio (${mon})</th>
          ${conEstado ? '<th style="text-align:center">Estado</th>' : ""}
        </tr>
        ${filasLineas}
      </table>
      <div class="total"><span class="monto">TOTAL: ${mon} ${fmtMoney(f.valor)}</span></div>
      ${resumenInicial}
      ${
        cuotas.length
          ? `<div class="cuotas-titulo">Plan de cuotas</div>
      <table class="cuotas">
        <tr><th style="width:50px">#</th><th>Fecha</th><th class="num">Valor (${mon})</th></tr>
        ${filasCuotas}
      </table>`
          : ""
      }
      <div class="firmas">
        <div>Entregado por${f.creado ? `<br/><b>${f.creado}</b>` : ""}</div>
        <div>Recibido por</div>
      </div>
      ${pagina.pie}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

// Conduce de entrega en consignación, en el mismo formato carta que la factura
// pero SIN nada fiscal: no lleva NCF ni "TOTAL a pagar", porque el cliente no
// debe nada todavía. Lleva el valor de la mercancía entregada (que es lo que
// respalda la firma del cliente), el estado de cada motor y, si ya hubo ventas,
// las facturas que salieron del conduce.
export function imprimirConduce(det) {
  const c = det.conduce || {};
  const compania = det.compania || {};
  const lineas = det.lineas || [];
  const facturas = det.facturas || [];
  const mon = c.monedaTexto || monedaLabel(c.moneda);
  const pagina = paginaConPie(compania);
  const NAVY = "#18586b";
  const ESTADOS = { "": "En piso", V: "Vendido", F: "Facturado", D: "Devuelto" };
  const filasLineas = lineas
    .map((l, i) => {
      const estado = String(l.estado || "").trim();
      const detalle =
        estado === "F" && l.factura
          ? `Facturado (#${l.factura})`
          : ESTADOS[estado] || estado || "En piso";
      return `<tr class="${estado === "D" ? "devuelto" : ""}">
          <td>${i + 1}</td>
          <td>${l.chasis || ""}</td>
          <td>${l.maquina || ""}</td>
          <td>${l.marca || ""}</td>
          <td>${l.modelo || ""}</td>
          <td>${l.colore || ""}</td>
          <td>${anoTexto(l.ano)}</td>
          <td class="num">${fmtMoney(l.precio)}</td>
          <td class="estado${estado === "V" ? " rojo" : ""}">${detalle}</td>
        </tr>`;
    })
    .join("");
  const filasFacturas = facturas
    .map(
      (f) => `<tr>
          <td>${f.id || ""}</td>
          <td>${f.fecha || ""}</td>
          <td>${f.ncf || ""}</td>
          <td class="num">${fmtMoney(f.valor)}</td>
        </tr>`
    )
    .join("");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Conduce Ventas x Mayor</title>
    <style>
      ${pagina.css}
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 90px; max-width: 250px; object-fit: contain; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .cliente { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                 display: flex; gap: 30px; flex-wrap: wrap; }
      .cliente span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .cliente b { font-size: 12px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 5px 7px; font-size: 10px; text-align: left; }
      table.lineas td { padding: 5px 7px; border-bottom: 1px solid #e3e9ee; font-size: 10px; }
      table.lineas tr:nth-child(even) td { background: #f5f8fa; }
      th.num, td.num { text-align: right; }
      td.estado { text-align: center; }
      td.rojo { color: #C62828; font-weight: bold; }
      tr.devuelto td { color: #999; text-decoration: line-through; }
      .total { margin: 12px 0; text-align: right; }
      .total .monto { display: inline-block; background: #f0f5f8; border: 1px solid #d7dee5; border-radius: 6px;
                      padding: 8px 16px; font-size: 14px; font-weight: bold; color: ${NAVY}; }
      .aviso { margin: 10px 0 0; padding: 8px 12px; border-left: 3px solid ${NAVY}; background: #f0f5f8;
               font-size: 11px; color: #4a5866; }
      .cuotas-titulo { margin: 16px 0 4px; font-weight: bold; color: ${NAVY}; font-size: 12px; text-transform: uppercase; }
      table.cuotas { width: 65%; border-collapse: collapse; }
      table.cuotas th { background: #f0f5f8; color: ${NAVY}; padding: 5px 8px; font-size: 11px; text-align: left; }
      table.cuotas td { padding: 4px 8px; border-bottom: 1px solid #e3e9ee; }
      table.cuotas th.num { text-align: right; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
      .pie { position: fixed; bottom: 0; left: 0; right: 0; }
      .pie img { display: block; width: 100%; }
      table.lineas tr, table.cuotas tr, table.resumen tr, .firmas { break-inside: avoid; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(compania)}
          <div>
            <h1>${compania.nombre || ""}</h1>
            ${compania.direccion ? `<p>${compania.direccion}</p>` : ""}
            <p>${[
              compania.rnc ? `RNC: ${compania.rnc}` : "",
              compania.telefono ? `Tel: ${compania.telefono}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Conduce de entrega en consignación</div>
          <p>No es una factura</p>
          <p># Conduce: <b>${c.id || ""}</b></p>
          <p>Fecha: <b>${c.fecha || c.fechatexto || ""}</b></p>
          ${c.estadoLabel ? `<p>Estado: <b>${c.estadoLabel}</b></p>` : ""}
        </div>
      </div>
      <div class="cliente">
        <div><span>Cliente</span><b>${c.clientenombre || c.cliente || ""}</b></div>
        ${c.rnc ? `<div><span>RNC</span><b>${c.rnc}</b></div>` : ""}
        ${c.concepto ? `<div><span>Concepto</span><b>${c.concepto}</b></div>` : ""}
        <div><span>Motores</span><b>${c.motores ?? lineas.length}</b></div>
      </div>
      <table class="lineas">
        <tr>
          <th style="width:28px">#</th>
          <th>Chasis</th>
          <th>Máquina</th>
          <th>Marca</th>
          <th>Modelo</th>
          <th>Color</th>
          <th>Año</th>
          <th class="num">Precio pactado (${mon})</th>
          <th style="text-align:center">Estado</th>
        </tr>
        ${filasLineas}
      </table>
      <div class="total"><span class="monto">MERCANCÍA ENTREGADA: ${mon} ${fmtMoney(
    c.valorpiso
  )}</span></div>
      <p class="aviso">Los vehículos descritos se entregan en consignación y siguen siendo
      propiedad de ${compania.nombre || "la empresa"} hasta que sean facturados. Este documento no
      constituye una venta ni genera obligación de pago; la factura se emite por los vehículos
      que el cliente reporte como vendidos.</p>
      ${
        facturas.length
          ? `<div class="cuotas-titulo">Facturas emitidas de este conduce</div>
      <table class="cuotas">
        <tr><th style="width:70px">Factura</th><th>Fecha</th><th>NCF</th><th class="num">Valor (${mon})</th></tr>
        ${filasFacturas}
      </table>`
          : ""
      }
      <div class="firmas">
        <div>Entregado por${c.creado ? `<br/><b>${c.creado}</b>` : ""}</div>
        <div>Recibido por</div>
      </div>
      ${pagina.pie}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

// Recibo de cobro x mayor (multi-factura con todas sus líneas).
export function imprimirReciboCobroXmayor(det) {
  const r = det.recibo || {};
  const compania = det.compania || {};
  const detalle = det.detalle || [];
  // Moneda del recibo (= la de la factura cobrada): rotula las columnas de
  // importe para que no haya que deducirla del total.
  const mon = monedaLabel(r.moneda);
  const filas = detalle
    .map(
      (d) =>
        `<tr>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${d.nfact || ""}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${etiquetaCuota(
            d.cuotacompromiso
          )}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
            d.valorpagado
          )}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
            d.balancefactura
          )}</td>
        </tr>`
    )
    .join("");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Recibo de Cobro Ventas x Mayor</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:480px;margin:0 auto">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Recibo de Cobro Ventas x Mayor</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px">
        <tr><td style="padding:3px 8px;color:#555"># Recibo</td><td style="padding:3px 8px;font-weight:bold">${
          r.id || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Fecha</td><td style="padding:3px 8px;font-weight:bold">${
          r.fecha || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Cliente</td><td style="padding:3px 8px;font-weight:bold">${
          r.cliente || ""
        }</td></tr>
      </table>
      <table style="width:100%;border-collapse:collapse;margin-bottom:8px">
        <tr>
          <th style="padding:3px 6px;text-align:left;border-bottom:1px solid #000">Factura</th>
          <th style="padding:3px 6px;text-align:left;border-bottom:1px solid #000">Cuota</th>
          <th style="padding:3px 6px;text-align:right;border-bottom:1px solid #000">Valor (${mon})</th>
          <th style="padding:3px 6px;text-align:right;border-bottom:1px solid #000">Balance factura (${mon})</th>
        </tr>
        ${filas}
      </table>
      <p style="text-align:right;font-weight:bold;margin:6px 0 2px">TOTAL: ${monedaLabel(
        r.moneda
      )} ${fmtMoney(r.valorpagado)}</p>
      ${
        r.equivalente
          ? `<p style="text-align:right;margin:0 0 14px">${monedaLabel(
              r.equivalentemoneda
            )} ${fmtMoney(r.equivalente)} (TASA ${fmtMoney(r.tasa)})</p>`
          : ""
      }
      <table style="width:100%;border-collapse:collapse;margin-top:10px">
        ${
          r.banco
            ? `<tr><td style="padding:3px 8px;color:#555">Forma de pago</td><td style="padding:3px 8px;font-weight:bold">${r.banco}</td></tr>`
            : ""
        }
        ${
          r.referencia
            ? `<tr><td style="padding:3px 8px;color:#555">Referencia</td><td style="padding:3px 8px;font-weight:bold">${r.referencia}</td></tr>`
            : ""
        }
        ${
          r.descripcion
            ? `<tr><td style="padding:3px 8px;color:#555">Descripción</td><td style="padding:3px 8px;font-weight:bold">${r.descripcion}</td></tr>`
            : ""
        }
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}
